import { IconButton } from "@/components/atoms/IconButton/IconButton";
import { IconButtonWithText } from "@/components/atoms/IconButtonWithText/IconButtonWithText";
import { FeatureGate } from "@/components/molecules/FeatureGate/FeatureGate";
import { OptionsMenu } from "@/components/molecules/OptionsMenu/OptionsMenu";
import { TooltipWrapper } from "@/components/molecules/TooltipWrapper/TooltipWrapper";
import { AddDashboardCard } from "@/dashboard/AddDashboardCard";
import { useDashboardListUI } from "@/dashboard/DashboardListUIProvider";
import { DashboardTagFilter } from "@/dashboard/DashboardTagFilter";
import { buildSortMenuItems } from "@/dashboard/utils";
import { useDesktop } from "@/overlay/components/window";
import { pageProfiles, startsWithExtraPath } from "@/route";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import React from "react";
import { useFormContext } from "react-hook-form";
import { useHistory, useRouteMatch } from "react-router-dom";
import type { FilterAndSorterFormTypes } from "./types";

export type DashboardHeaderProps = {
  onSortChange?: (sort: FilterAndSorterFormTypes["sort"]) => void;
  showCollapseButton?: undefined | boolean;
  isSideNav?: boolean;
};

export function DashboardHeader({ onSortChange, showCollapseButton, isSideNav }: DashboardHeaderProps) {
  const desktop = useDesktop();
  const match = useRouteMatch();
  const history = useHistory();

  const { expandAll, toggleExpandAll } = useDashboardListUI();

  const { register, setValue, watch } = useFormContext<FilterAndSorterFormTypes>();
  const activeSort = watch("sort");

  const filterOptions = { title: t`Title`, time_created: t`Creation Date`, time_updated: t`Edit Date` };

  return (
    <div className="dashboards__header">
      <div className="dashboards__header-title">
        <div className="profile-area-content__heading" data-testid="profile-heading">
          {isSideNav ? (
            <TooltipWrapper tooltipText={t`View all dashboards`} position="right" tail={true} delay={700}>
              <IconButtonWithText
                iconName="home"
                label={t`Your Dashboards`}
                aria-label={"Home"}
                data-testid="dashboard-home-btn"
                onClick={() => {
                  desktop.dispatch({
                    type: "setAllWindowVisibility",
                    isVisible: false,
                  });
                  history.push(`${startsWithExtraPath(match.path)}${pageProfiles.asString()}`);
                }}
              />
            </TooltipWrapper>
          ) : (
            <Trans id="profiles.heading.your-dashboard">Your Dashboards</Trans>
          )}
        </div>
        <div className="dashboards__header-unfold">
          {showCollapseButton && (
            <TooltipWrapper
              tooltipText={expandAll ? t`Collapse all` : t`Expand all`}
              position="left"
              tail={true}
              delay={700}
            >
              <IconButton
                iconName={expandAll ? "unfold_less" : "unfold_more"}
                ariaLabel={expandAll ? t`Collapse all` : t`Expand all`}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleExpandAll();
                }}
              />
            </TooltipWrapper>
          )}
          <TooltipWrapper tooltipText={t`Sort menu`} position="left" tail={true} delay={700}>
            <OptionsMenu
              iconName="list"
              ariaLabel={t`Sort`}
              className={""}
              items={buildSortMenuItems(filterOptions, activeSort, setValue, onSortChange)}
            />
          </TooltipWrapper>
        </div>
      </div>
      <div className="dashboards__header-control">
        <div className="search-wrapper">
          <span className="material-icons search-icon">search</span>
          <input
            style={{ padding: "8px", paddingLeft: "36px", height: "100%" }}
            className="search-input"
            data-testid="dashboard-search-input"
            type="search"
            placeholder={t`Search by name`}
            {...register("query")}
          />
        </div>
        <FeatureGate path={"ui.dashboard.add"}>
          <TooltipWrapper tooltipText={t`Create new Dashboard`} position="right" tail={true} delay={700}>
            <AddDashboardCard maximized={true} />
          </TooltipWrapper>
        </FeatureGate>
      </div>
      <FeatureGate path={"ui.dashboard.edit"}>
        <DashboardTagFilter isSideNav={isSideNav} />
      </FeatureGate>
    </div>
  );
}
